<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { useMessageContext } from '../provider.js';
import AudioChip from './Audio.vue';

const props = defineProps({
  attachment: { type: Object, required: true },
});

const { id, conversationId, contentType, contentAttributes } =
  useMessageContext();
const { currentAccount, accountId } = useAccount();
const store = useStore();
const { t } = useI18n();
const isRequesting = ref(false);
const requestError = ref('');
const isComplete = computed(() =>
  Boolean(
    props.attachment.transcriptionComplete ||
      props.attachment.transcribedText ||
      props.attachment.transcriptionStatus === 'completed'
  )
);
const canTranscribe = computed(() =>
  Boolean(
    currentAccount.value?.settings?.audio_transcriptions &&
      Number.isInteger(Number(id.value)) &&
      Number(id.value) > 0 &&
      Number(conversationId.value) > 0 &&
      Number(props.attachment.id) > 0 &&
      Number(props.attachment.messageId) === Number(id.value) &&
      Number(props.attachment.accountId) === accountId.value &&
      props.attachment.fileType === 'audio' &&
      contentType.value !== 'voice_call' &&
      !contentAttributes.value?.deleted &&
      !isComplete.value
  )
);
const isQueued = computed(() =>
  ['pending', 'processing'].includes(props.attachment.transcriptionStatus)
);
const isDelayed = ref(false);
let delayedTimer;
const clearDelayedTimer = () => window.clearTimeout(delayedTimer);
watch(
  () => [isQueued.value, props.attachment.transcriptionRequestedAt],
  () => {
    clearDelayedTimer();
    isDelayed.value = false;
    const requestedAt = Date.parse(props.attachment.transcriptionRequestedAt);
    if (!isQueued.value || !Number.isFinite(requestedAt)) return;
    const remaining = requestedAt + 15 * 60 * 1000 - Date.now();
    if (remaining <= 0) {
      isDelayed.value = true;
    } else {
      // Only reveal a manual retry; elapsed time never requests transcription.
      delayedTimer = window.setTimeout(() => {
        isDelayed.value = true;
      }, remaining);
    }
  },
  { immediate: true }
);
onBeforeUnmount(clearDelayedTimer);
const isTranscribing = computed(
  () => isRequesting.value || (isQueued.value && !isDelayed.value)
);
const transcriptionError = computed(
  () =>
    requestError.value ||
    (isDelayed.value ? t('CONVERSATION.AUDIO_TRANSCRIPTION.DELAYED') : '') ||
    (props.attachment.transcriptionStatus === 'failed'
      ? props.attachment.transcriptionError ||
        t('CONVERSATION.AUDIO_TRANSCRIPTION.ERROR')
      : '')
);

watch(
  () => [props.attachment.id, props.attachment.transcriptionStatus],
  () => {
    requestError.value = '';
  }
);

const transcribe = async () => {
  if (!canTranscribe.value || isTranscribing.value) return;
  isRequesting.value = true;
  requestError.value = '';
  try {
    await store.dispatch('transcribeAudioMessage', {
      conversationId: conversationId.value,
      messageId: id.value,
      attachmentId: props.attachment.id,
    });
  } catch (error) {
    const status = error.response?.status;
    const message = error.response?.data?.error;
    if (status === 422 && typeof message === 'string') {
      requestError.value = message;
    } else if (status === 403 || status === 404) {
      requestError.value = t('CONVERSATION.AUDIO_TRANSCRIPTION.UNAVAILABLE');
    } else {
      requestError.value = t('CONVERSATION.AUDIO_TRANSCRIPTION.ERROR');
    }
  } finally {
    isRequesting.value = false;
  }
};
</script>

<template>
  <AudioChip
    :attachment="attachment"
    :can-transcribe="canTranscribe"
    :is-transcribing="isTranscribing"
    :transcription-error="transcriptionError"
    @transcribe="transcribe"
  />
</template>
